import { inject, Injectable } from "@angular/core";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { isFgdsProject } from "@domain/project/project-guards";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { HalfPageContainerService } from "@pdf/helpers/container/half-page/half-page-container.service";
import { TableService } from "@pdf/helpers/default-table/table-service";
import { MAX_HEADLINE_WIDTH, truncateTextToWidth } from "@pdf/helpers/pdf-text.helper";
import { Headlines, ImagePaths, PdfProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { ProductNotes, ProductTypes } from "./product-notes";
import { ProductNotesTable } from "./product-notes-table";

@Injectable({
  providedIn: "root",
})
export class ProductNotesPdfService {
  private containerService = inject(HalfPageContainerService);

  private tableService!: TableService;
  private headlines = new Headlines();

  public generatePages(pdf: jsPDF, floorplan: Floorplan, container: HalfPageContainer, tableOfContents: TableOfContents) {
    if (!isFgdsProject(floorplan.project)) return;
    const productNotes = new ProductNotes(floorplan, floorplan.project);
    if (productNotes.hasNotes) {
      this.tableService = new TableService();
      const prefix = `${this.headlines.PRODUCT_NOTES} | `;
      pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
      const truncatedName = truncateTextToWidth(pdf, floorplan.name, MAX_HEADLINE_WIDTH - pdf.getTextWidth(prefix));
      const headline = `${prefix}${truncatedName}`;

      this.containerService.addOnNewPage(pdf, container, headline, tableOfContents);

      for (const group of productNotes.groups) {
        for (const section of group.sections) {
          if (section.rows.length > 0) {
            this.tableService.tableToPdf(
              pdf,
              new ProductNotesTable(
                pdf,
                section.rows,
                this.getImagePath(section.productType)!,
                `${this.getHeadline(section.productType)} - ${group.name}`,
              ),
              container,
              tableOfContents,
              headline,
            );
          }
        }
      }

      for (const section of productNotes.defaultNotesGroup.sections) {
        if (section.rows.length > 0) {
          this.tableService.tableToPdf(
            pdf,
            new ProductNotesTable(
              pdf,
              section.rows,
              this.getImagePath(section.productType)!,
              `${this.getHeadline(section.productType)} - ${productNotes.defaultNotesGroup.name}`,
            ),
            container,
            tableOfContents,
            headline,
          );
        }
      }
    }
  }
  private getImagePath(productType: string) {
    switch (productType) {
      case ProductTypes.GAS_WARNING_CENTER: {
        return ImagePaths.GASWARNINGCENTER;
      }
      case ProductTypes.GAS_WARNING_CENTER_PLACEHOLDER: {
        return ImagePaths.GASWARNINGCENTER_PLACEHOLDER;
      }
      case ProductTypes.TRANSMITTER: {
        return ImagePaths.TRANSMITTER;
      }
      case ProductTypes.TRANSMITTER_PLACEHOLDER: {
        return ImagePaths.TRANSMITTER_PLACEHOLDER;
      }
      case ProductTypes.FLAME_DETECTOR: {
        return ImagePaths.FLAME_DETECTOR;
      }
      case ProductTypes.FLAME_DETECTOR_PLACEHOLDER: {
        return ImagePaths.FLAME_DETECTOR_PLACEHOLDER;
      }
      case ProductTypes.ALARM_DEVICE: {
        return ImagePaths.ALARMDEVICE;
      }
      case ProductTypes.ALARM_DEVICE_PLACEHOLDER: {
        return ImagePaths.ALARMDEVICE_PLACEHOLDER;
      }
      case ProductTypes.SIGNAL_ELEMENT: {
        return ImagePaths.SIGNALELEMENT;
      }
      case ProductTypes.SIGNALELEMENT_PLACEHOLDER: {
        return ImagePaths.SIGNALELEMENT_PLACEHOLDER;
      }
      case ProductTypes.PLASTICSIGN: {
        return ImagePaths.PLASTICSIGN;
      }
      case ProductTypes.PLASTICSIGN_PLACEHOLDER: {
        return ImagePaths.PLASTICSIGN_PLACEHOLDER;
      }
    }
    return undefined;
  }

  private getHeadline(productType: string) {
    switch (productType) {
      case ProductTypes.GAS_WARNING_CENTER: {
        return this.headlines.GASWARNINGCENTERS;
      }
      case ProductTypes.GAS_WARNING_CENTER_PLACEHOLDER: {
        return this.headlines.GASWARNINGCENTERS_PLACEHOLDERS;
      }
      case ProductTypes.TRANSMITTER: {
        return this.headlines.TRANSMITTERS;
      }
      case ProductTypes.TRANSMITTER_PLACEHOLDER: {
        return this.headlines.TRANSMITTERS_PLACEHOLDERS;
      }
      case ProductTypes.FLAME_DETECTOR: {
        return this.headlines.FLAME_DETECTORS;
      }
      case ProductTypes.FLAME_DETECTOR_PLACEHOLDER: {
        return this.headlines.FLAME_DETECTOR_PLACEHOLDERS;
      }
      case ProductTypes.ALARM_DEVICE: {
        return this.headlines.ALARMDEVICES;
      }
      case ProductTypes.ALARM_DEVICE_PLACEHOLDER: {
        return this.headlines.ALARMDEVICES_PLACEHOLDERS;
      }
      case ProductTypes.SIGNAL_ELEMENT: {
        return this.headlines.SIGNALELEMENTS;
      }
      case ProductTypes.SIGNALELEMENT_PLACEHOLDER: {
        return this.headlines.SIGNALELEMENTS_PLACEHOLDERS;
      }
      case ProductTypes.PLASTICSIGN: {
        return this.headlines.PLASTICSIGNS;
      }
      case ProductTypes.PLASTICSIGN_PLACEHOLDER: {
        return this.headlines.PLASTICSIGNS_PLACEHOLDERS;
      }
    }
    return "";
  }
}
